import React, { useState, useEffect } from 'react';
import { Users, FolderKanban, Briefcase } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Card from '../../components/Card';
import TabNavigation from '../../components/TabNavigation';
import { ProjectRole, ProgrammeRole } from '../../types/role';
import { getProjectRoles, getProgrammeRoles } from '../../api/roles';
import { getProjects } from '../../api/projects';
import { getProgrammes } from '../../api/programmes';
import { getAllUsers } from '../../api/users';
import { Project } from '../../types/project';
import { Programme } from '../../types/programme';
import { User } from '../../types/user';

/**
 * ProjectRolesPage - Read-only audit of granular project & programme access.
 *
 * Assigning, changing, and removing access roles is done per-user in
 * "Manage Access" (Users → Manage Access). This page is a read-only overview of
 * who currently has Editor/Viewer access to which project or programme.
 */
const ProjectRolesPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'project' | 'programme'>('project');
    const [projectRoles, setProjectRoles] = useState<ProjectRole[]>([]);
    const [programmeRoles, setProgrammeRoles] = useState<ProgrammeRole[]>([]);
    const [projects, setProjects] = useState<Project[]>([]);
    const [programmes, setProgrammes] = useState<Programme[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [projectRolesData, programmeRolesData, projectsData, programmesData, usersData] = await Promise.all([
                getProjectRoles(),
                getProgrammeRoles(),
                getProjects(),
                getProgrammes(),
                getAllUsers()
            ]);

            setProjectRoles(projectRolesData);
            setProgrammeRoles(programmeRolesData);
            setProjects(projectsData);
            setProgrammes(programmesData);
            setUsers(usersData);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to load data';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    // Helper functions to get names
    const getUserName = (userId: string): string => {
        const user = users.find(u => u.id === userId);
        return user ? (user.full_name || user.username) : `User ${userId}`;
    };

    const getProjectName = (projectId: string): string => {
        const project = projects.find(p => String(p.id) === String(projectId));
        return project ? project.name : `Project ${projectId}`;
    };

    const getProgrammeName = (programmeId: string): string => {
        const programme = programmes.find(p => String(p.id) === String(programmeId));
        return programme ? programme.name : `Programme ${programmeId}`;
    };

    if (isLoading) {
        return (
            <div className="loading-container min-h-screen">
                <div className="spinner" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8">
                <div className="bg-error/10 dark:bg-error/20 border border-error/30 text-error rounded-lg p-4">
                    <p className="text-error">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                    <Briefcase className="w-8 h-8 text-primary-600" />
                    Access Audit
                </h1>
                <p className="mt-2 text-gray-600 dark:text-gray-400">
                    Read-only overview of who has Editor or Viewer access to each project and programme.
                    To grant, change, or remove access, open <span className="font-medium">Users → Manage Access</span>.
                </p>
            </div>

            {/* Tabs */}
            <TabNavigation
                tabs={[
                    { id: 'project', label: `Project Roles (${projectRoles.length})`, icon: FolderKanban },
                    { id: 'programme', label: `Programme Roles (${programmeRoles.length})`, icon: Users }
                ]}
                activeTab={activeTab}
                onTabChange={(tabId) => setActiveTab(tabId as 'project' | 'programme')}
                className="mb-6"
            />

            {/* Project Roles Table */}
            {activeTab === 'project' && (
                <Card>
                    <Card.Header title="Project Role Assignments" />
                    <Card.Body>
                        {projectRoles.length === 0 ? (
                            <div className="text-center py-12">
                                <div className="text-5xl mb-4">📁</div>
                                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">No project roles assigned yet</h3>
                                <p className="text-gray-600 dark:text-gray-400">Grant project access from Users → Manage Access.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-gray-50 dark:bg-gray-800">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                User
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                Project
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                Role
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                Assigned
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                        {projectRoles.map((role) => (
                                            <tr key={role.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                        {getUserName(role.user_id)}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900 dark:text-white">
                                                        {getProjectName(role.project_id)}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                        role.role === 'Editor'
                                                            ? 'bg-secondary-100 text-secondary-800 dark:bg-secondary-900/30 dark:text-secondary-300'
                                                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                                    }`}>
                                                        {role.role}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                    {new Date(role.assigned_at).toLocaleDateString()}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            )}

            {/* Programme Roles Table */}
            {activeTab === 'programme' && (
                <Card>
                    <Card.Header title="Programme Role Assignments" />
                    <Card.Body>
                        {programmeRoles.length === 0 ? (
                            <div className="text-center py-12">
                                <div className="text-5xl mb-4">👥</div>
                                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">No programme roles assigned yet</h3>
                                <p className="text-gray-600 dark:text-gray-400">Grant programme access from Users → Manage Access.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-gray-50 dark:bg-gray-800">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                User
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                Programme
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                Role
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                Assigned
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                        {programmeRoles.map((role) => (
                                            <tr key={role.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                        {getUserName(role.user_id)}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900 dark:text-white">
                                                        {getProgrammeName(role.programme_id)}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                        role.role === 'Editor'
                                                            ? 'bg-accent-100 text-accent-800 dark:bg-accent-900/30 dark:text-accent-300'
                                                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                                    }`}>
                                                        {role.role}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                    {new Date(role.assigned_at).toLocaleDateString()}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            )}
        </div>
    );
};

export default ProjectRolesPage;
